import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Sparkles, Plus, Trash2, Save, Mail, Globe, Megaphone, Compass, Scissors, Plane, Users, Box, Calendar, Zap, HelpCircle, Heart, Star, Home, Building, MoreHorizontal } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Switch } from "./ui/switch";
import { Label } from "./ui/label";
import { Card } from "./ui/card";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ICON_OPTIONS = [
  { value: "globe", Icon: Globe, label: "Globus" },
  { value: "megaphone", Icon: Megaphone, label: "Megaphon" },
  { value: "compass", Icon: Compass, label: "Kompass" },
  { value: "sparkles", Icon: Sparkles, label: "Sterne" },
  { value: "scissors", Icon: Scissors, label: "Schere" },
  { value: "plane", Icon: Plane, label: "Flugzeug" },
  { value: "users", Icon: Users, label: "Gruppe" },
  { value: "box", Icon: Box, label: "Box" },
  { value: "calendar", Icon: Calendar, label: "Kalender" },
  { value: "zap", Icon: Zap, label: "Blitz" },
  { value: "help", Icon: HelpCircle, label: "Fragezeichen" },
  { value: "heart", Icon: Heart, label: "Herz" },
  { value: "star", Icon: Star, label: "Stern" },
  { value: "home", Icon: Home, label: "Haus" },
  { value: "building", Icon: Building, label: "Gebäude" },
  { value: "dots", Icon: MoreHorizontal, label: "Weiteres" },
];

const ICON_MAP = Object.fromEntries(ICON_OPTIONS.map(o => [o.value, o.Icon]));

function IconPicker({ value, onChange }) {
  const [open, setOpen] = useState(false);
  const Selected = ICON_MAP[value] || MoreHorizontal;
  return (
    <div className="relative">
      <button type="button" onClick={() => setOpen(o => !o)} className="h-9 w-9 rounded-md border bg-background flex items-center justify-center hover:bg-muted transition" data-testid={`icon-picker-${value}`}>
        <Selected size={16} />
      </button>
      {open && (
        <div className="absolute z-20 top-full mt-1 left-0 bg-popover border rounded-md shadow-lg p-2 grid grid-cols-4 gap-1 w-48">
          {ICON_OPTIONS.map(({ value: v, Icon, label }) => (
            <button key={v} type="button" onClick={() => { onChange(v); setOpen(false); }} title={label} className={`h-9 w-9 rounded flex items-center justify-center hover:bg-muted transition ${v === value ? "bg-muted ring-2 ring-primary" : ""}`}>
              <Icon size={15} />
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function CardEditor({ items, onChange, label, maxItems = 6, minItems = 1, showDescription = true, testIdPrefix = "card" }) {
  const update = (idx, patch) => {
    const next = items.map((it, i) => (i === idx ? { ...it, ...patch } : it));
    onChange(next);
  };
  const remove = idx => {
    if (items.length <= minItems) { toast.error(`Mindestens ${minItems} Einträge erforderlich`); return; }
    onChange(items.filter((_, i) => i !== idx));
  };
  const add = () => {
    if (items.length >= maxItems) { toast.error(`Maximal ${maxItems} Einträge`); return; }
    onChange([...items, { label: "Neue Option", icon: "dots", description: "" }]);
  };
  return (
    <div className="space-y-2">
      <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">{label}</Label>
      <div className="space-y-2">
        {items.map((it, i) => (
          <div key={i} className="flex items-start gap-2 p-2 rounded-md border bg-card" data-testid={`${testIdPrefix}-row-${i}`}>
            <IconPicker value={it.icon || "dots"} onChange={v => update(i, { icon: v })} />
            <div className="flex-1 space-y-1">
              <Input value={it.label || ""} onChange={e => update(i, { label: e.target.value })} placeholder="Label" className="h-8 text-sm" data-testid={`${testIdPrefix}-label-${i}`} />
              {showDescription && (
                <Input value={it.description || ""} onChange={e => update(i, { description: e.target.value })} placeholder="Kurzbeschreibung (optional)" className="h-8 text-xs" data-testid={`${testIdPrefix}-desc-${i}`} />
              )}
            </div>
            <Button type="button" size="sm" variant="ghost" onClick={() => remove(i)} className="text-red-600 hover:text-red-700 h-8 px-2" data-testid={`${testIdPrefix}-remove-${i}`}><Trash2 size={13} /></Button>
          </div>
        ))}
      </div>
      <Button type="button" size="sm" variant="outline" onClick={add} className="w-full h-8" data-testid={`${testIdPrefix}-add`}><Plus size={13} className="mr-1" /> Eintrag hinzufügen</Button>
    </div>
  );
}

export function ProjectInquiryEditor({ projectId }) {
  const [cfg, setCfg] = useState(null);
  const [inquiries, setInquiries] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!projectId) return;
    (async () => {
      try {
        const [c, i] = await Promise.all([
          axios.get(`${API}/projects/${projectId}/inquiry-config`, { withCredentials: true }),
          axios.get(`${API}/projects/${projectId}/inquiries`, { withCredentials: true }).catch(() => ({ data: [] })),
        ]);
        setCfg(c.data);
        setInquiries(i.data || []);
      } catch (e) {
        toast.error("Konnte Anfrage-Konfiguration nicht laden");
      } finally {
        setLoading(false);
      }
    })();
  }, [projectId]);

  const save = async () => {
    setSaving(true);
    try {
      const res = await axios.put(`${API}/projects/${projectId}/inquiry-config`, cfg, { withCredentials: true });
      setCfg(res.data);
      toast.success("Einstellungen gespeichert — Website wird mit neuem Widget neu generiert.");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) return <div className="p-8 text-sm text-muted-foreground">Lade…</div>;

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-8" data-testid="project-inquiry-editor">
      <div className="flex items-start justify-between gap-4 pb-6 border-b">
        <div>
          <h2 className="text-xl font-bold mb-1 flex items-center gap-2"><Sparkles size={18} className="text-yellow-500" /> Projekt-Anfrage-Wizard</h2>
          <p className="text-sm text-muted-foreground max-w-xl">Mehrstufiges Kontaktformular für strukturierte Anfragen. Erscheint als Floating-Button unten rechts auf der gerenderten Website.</p>
        </div>
        <div className="flex items-center gap-3 pt-1">
          <Label htmlFor="pi-enabled" className="text-sm font-medium">Aktiviert</Label>
          <Switch id="pi-enabled" checked={cfg.enabled} onCheckedChange={v => setCfg(c => ({ ...c, enabled: v }))} data-testid="inquiry-enabled-switch" />
        </div>
      </div>

      {/* Empfangene Anfragen */}
      {inquiries.length > 0 && (
        <Card className="p-5 bg-gradient-to-br from-yellow-50 to-orange-50 border-yellow-200/50 dark:from-yellow-950/20 dark:to-orange-950/20">
          <div className="flex items-center gap-2 mb-4 text-sm font-semibold"><Mail size={14} /> {inquiries.length} Anfrage{inquiries.length !== 1 ? "n" : ""} empfangen</div>
          <div className="space-y-2">
            {inquiries.slice(0, 5).map((inq, idx) => (
              <div key={inq.id || idx} className="p-3 rounded-md bg-background border text-sm space-y-1" data-testid={`inquiry-item-${idx}`}>
                <div className="flex items-center justify-between">
                  <span className="font-semibold">{inq.contact_name} <span className="text-muted-foreground font-normal">· {inq.contact_company || "—"}</span></span>
                  <span className="text-xs text-muted-foreground">{new Date(inq.created_at).toLocaleString("de-DE")}</span>
                </div>
                <div className="text-xs text-muted-foreground">
                  <span className="font-medium text-foreground">{inq.project_type}</span> · {inq.product} · <span className="font-medium">{inq.volume}</span> · {inq.timing}
                </div>
                <div className="text-xs text-muted-foreground truncate">{inq.description}</div>
                <div className="text-xs flex gap-3">
                  <a href={`mailto:${inq.contact_email}`} className="text-blue-600 hover:underline">{inq.contact_email}</a>
                  {inq.contact_phone && <span className="text-muted-foreground">{inq.contact_phone}</span>}
                </div>
              </div>
            ))}
          </div>
        </Card>
      )}

      {/* Titel */}
      <div className="space-y-2">
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Modal-Titel</Label>
        <Input value={cfg.title} onChange={e => setCfg(c => ({ ...c, title: e.target.value }))} className="h-9" data-testid="inquiry-title-input" />
      </div>

      {/* Step 1: Projekttypen */}
      <CardEditor items={cfg.project_types} onChange={v => setCfg(c => ({ ...c, project_types: v }))} label="Schritt 1 — Projekttypen (2–4 Karten)" minItems={1} maxItems={4} testIdPrefix="pt" />

      {/* Step 2: Produkte */}
      <CardEditor items={cfg.products} onChange={v => setCfg(c => ({ ...c, products: v }))} label="Schritt 2 — Produkte / Leistungen (1–6 Karten)" minItems={1} maxItems={6} testIdPrefix="prod" />

      {/* Step 3: Volumen-Slider */}
      <Card className="p-4 space-y-3">
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Schritt 3 — Volumen-Slider</Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Frage</Label>
            <Input value={cfg.volume?.label || ""} onChange={e => setCfg(c => ({ ...c, volume: { ...c.volume, label: e.target.value } }))} className="h-9" data-testid="volume-label" />
          </div>
          <div>
            <Label className="text-xs">Einheit (z.B. Sales, Projekte, Termine)</Label>
            <Input value={cfg.volume?.unit || ""} onChange={e => setCfg(c => ({ ...c, volume: { ...c.volume, unit: e.target.value } }))} className="h-9" data-testid="volume-unit" />
          </div>
          <div>
            <Label className="text-xs">Minimum</Label>
            <Input type="number" value={cfg.volume?.min ?? 1} onChange={e => setCfg(c => ({ ...c, volume: { ...c.volume, min: +e.target.value } }))} className="h-9" data-testid="volume-min" />
          </div>
          <div>
            <Label className="text-xs">Maximum</Label>
            <Input type="number" value={cfg.volume?.max ?? 50} onChange={e => setCfg(c => ({ ...c, volume: { ...c.volume, max: +e.target.value } }))} className="h-9" data-testid="volume-max" />
          </div>
          <div>
            <Label className="text-xs">Startwert</Label>
            <Input type="number" value={cfg.volume?.default ?? 5} onChange={e => setCfg(c => ({ ...c, volume: { ...c.volume, default: +e.target.value } }))} className="h-9" data-testid="volume-default" />
          </div>
          <div>
            <Label className="text-xs">Schritt</Label>
            <Input type="number" value={cfg.volume?.step ?? 1} min="1" onChange={e => setCfg(c => ({ ...c, volume: { ...c.volume, step: +e.target.value } }))} className="h-9" data-testid="volume-step" />
          </div>
        </div>
      </Card>

      {/* Step 4: Timing */}
      <CardEditor items={cfg.timings} onChange={v => setCfg(c => ({ ...c, timings: v }))} label="Schritt 4 — Timing-Optionen (2–6 Karten)" minItems={1} maxItems={6} showDescription={false} testIdPrefix="timing" />

      {/* Step 5: Beschreibungs-Label */}
      <div className="space-y-3">
        <Label className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Schritt 5 — Beschreibungs-Feld</Label>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <div>
            <Label className="text-xs">Frage</Label>
            <Input value={cfg.description_label} onChange={e => setCfg(c => ({ ...c, description_label: e.target.value }))} className="h-9" data-testid="desc-label" />
          </div>
          <div>
            <Label className="text-xs">Platzhalter</Label>
            <Input value={cfg.description_placeholder} onChange={e => setCfg(c => ({ ...c, description_placeholder: e.target.value }))} className="h-9" data-testid="desc-placeholder" />
          </div>
        </div>
      </div>

      {/* Submit & Success */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3 pb-4 border-b">
        <div>
          <Label className="text-xs">Submit-Button-Text</Label>
          <Input value={cfg.submit_label} onChange={e => setCfg(c => ({ ...c, submit_label: e.target.value }))} className="h-9" data-testid="submit-label" />
        </div>
        <div>
          <Label className="text-xs">Erfolgs-Nachricht</Label>
          <Textarea value={cfg.success_message} onChange={e => setCfg(c => ({ ...c, success_message: e.target.value }))} className="text-sm" rows={2} data-testid="success-message" />
        </div>
      </div>

      <div className="sticky bottom-4 flex justify-end">
        <Button onClick={save} disabled={saving} className="gap-2 shadow-lg" data-testid="save-inquiry-config">
          {saving ? "Speichere..." : <><Save size={14} /> Einstellungen speichern</>}
        </Button>
      </div>
    </div>
  );
}

export default ProjectInquiryEditor;
